/**
 * Phase 37 — what the text mix does to TODAY's real drops, from the day
 * preview and the draft being edited: each drop's kind (the engine's own
 * rule), whether the fill may still touch it (sent, staged or skipped ones
 * are locked), and — through the same shared functions the fill runs — the
 * texts it sends: how many proven (and which pool ranks, taken in turn) and
 * how many fresh AI ones. Pure: the editor re-computes it on every click.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  type DayPlanContainerRow,
  type DayPlanDropRow,
  type DayPlanPreview,
  DROP_GEN_DROP_KINDS,
  type DropGenDropKind,
  type DropGenPlanDetail,
  type DropGenTextMix,
  type DropGenTextMixInput,
  dropKindOf,
  effectiveMix,
  keepsCopiedTexts,
} from '@trafficpulse/shared';

export type TodayLock = 'sent' | 'staged' | 'skipped' | null;

export interface TodayDrop {
  minute: number;
  containerName: string;
  listName: string | null;
  kind: DropGenDropKind;
  locked: TodayLock;
  pinned: boolean;
  /** What it sends: proven texts from the pool + fresh AI texts. */
  mix: DropGenTextMix;
  /** The pool ranks of its proven texts, in order. */
  ranks: number[];
  /** Proven texts the pool could not give — they became AI texts. */
  short: number;
  /** Phase 38 — a hit-again drop keeping the texts of the drop that hit (no mix of its own). */
  copied?: boolean;
}

export interface TodayFacts {
  /** Every drop of the day in time order. */
  drops: TodayDrop[];
  /** True once the day was generated (runtime rows exist). */
  generated: boolean;
  poolSize: number;
}

const lockOf = (d: DayPlanDropRow): TodayLock => (d.runtime?.sentAt ? 'sent' : d.runtime?.staged ? 'staged' : d.runtime?.tombstoned ? 'skipped' : null);

/** The preview rows this draft is about: the container's own, or a master's followers'. */
export function todayRows(preview: DayPlanPreview | undefined, plan: DropGenPlanDetail | undefined, draft: { kind: string }, containerId: string | null): DayPlanContainerRow[] | null {
  if (!preview || containerId === null) return null;
  if (draft.kind !== 'MASTER') return preview.containers.filter((r) => r.containerId === containerId);
  const followers = new Set((plan?.containers ?? []).filter((c) => c.masterId === containerId).map((c) => c.id));
  return preview.containers.filter((r) => followers.has(r.containerId));
}

/** The pool ranks the k-th proven-taking draft gets (the fill's own turn rule). */
export const provenRanks = (pool: Array<{ rank: number | null }>, k: number, proven: number): number[] =>
  Array.from({ length: Math.min(proven, pool.length) }, (_, j) => pool[(k * proven + j) % pool.length].rank ?? 0);

/** The fill's own arithmetic on today's drops, container by container, in time order. */
export function todayFacts(rows: DayPlanContainerRow[], cfg: DropGenTextMixInput, pool: Array<{ rank: number | null }>): TodayFacts {
  const drops: TodayDrop[] = [];
  let generated = false;
  for (const row of rows) {
    let turn = 0;
    for (const d of [...row.drops].sort((a, b) => a.minute - b.minute)) {
      if (d.runtime) generated = true;
      const kind = dropKindOf(d);
      const locked = lockOf(d);
      // Phase 38 — a hit-again drop keeps the texts of the drop that hit: no mix, no turn.
      const copied = keepsCopiedTexts(cfg, kind);
      const eff = copied ? { proven: 0, ai: 0, short: 0 } : effectiveMix(cfg, kind, pool.length);
      const ranks = locked === null && eff.proven > 0 ? provenRanks(pool, turn++, eff.proven) : [];
      drops.push({ minute: d.minute, containerName: row.name, listName: d.listName, kind, locked, pinned: d.runtime?.pinned === true, mix: { proven: eff.proven, ai: eff.ai }, ranks, short: eff.short, ...(copied ? { copied: true } : {}) });
    }
  }
  drops.sort((a, b) => a.minute - b.minute);
  return { drops, generated, poolSize: pool.length };
}

export const isKind = (k: string): k is DropGenDropKind => (DROP_GEN_DROP_KINDS as readonly string[]).includes(k);

export const hhmm = (minute: number): string => `${String(Math.floor(minute / 60) % 24).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
